import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, ScanLine, Loader2, Search, CheckCircle2, XCircle, Clock, Trophy, PackageCheck, Undo2, RotateCcw, Phone, Mail, ReceiptText, Info, Gift, FileDown } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { BasarNav } from "../components/basar/BasarNav";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import { api, errorMessage, formatDateTime, imageSrc } from "../lib/api";
import { basarPaymentLabel, dashboardBasarCheckUrl, dashboardBasarUrl, nok, useMyBasar } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

// /dashboard/basar/:id/kontroll[/:orderId] – utlevering av premiar: skann QR-koden på kjøparen si kvittering (eller skriv inn
// ordrenummer/loddnummer), sjå lodd + gevinstar og merk kvar premie som utlevert. QR-en peikar hit med ordre-id i adressa.
export default function BasarCheckPage() {
  const { t } = useLang();
  const { id, orderId } = useParams();
  const navigate = useNavigate();
  const { basar, loading, error, setBasar } = useMyBasar(id);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [busySlot, setBusySlot] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const scannerRef = useRef(null);
  const lockRef = useRef(false);
  const loadedRef = useRef(""); // ordre-id som alt er lasta – hindrar dobbelt oppslag når adressa blir oppdatert etter eit treff

  useEffect(() => {
    if (error === "notfound") navigate("/dashboard/basar");
  }, [error, navigate]);

  const lookup = useCallback(
    async (value, { fromScan = false } = {}) => {
      const needle = (value || "").trim();
      if (!needle) return;
      setChecking(true);
      setCheckError("");
      try {
        const { data } = await api.get(`/my/basar/${id}/check`, { params: { code: needle } });
        setResult(data.order);
        loadedRef.current = data.order?.id || "";
        if (data.order?.id && data.order.id !== orderId) navigate(dashboardBasarCheckUrl(id, data.order.id), { replace: true });
        if (fromScan) toast.success(t("Kvittering funnet"));
      } catch (err) {
        setResult(null);
        loadedRef.current = "";
        setCheckError(errorMessage(err));
      } finally {
        setChecking(false);
        lockRef.current = false;
      }
    },
    [id, orderId, navigate, t]
  );

  // Direkte frå QR-koden: /kontroll/:orderId (ikkje på nytt når adressa berre speglar eit treff vi alt har lasta)
  useEffect(() => {
    if (orderId && loadedRef.current !== orderId) lookup(orderId);
    else if (!orderId) {
      setResult(null);
      loadedRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, id]);

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    if (scanner) {
      try {
        if (scanner.isScanning) await scanner.stop();
        scanner.clear();
      } catch {
        // ignorer
      }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const handleScan = useCallback(
    (decodedText) => {
      if (lockRef.current) return;
      lockRef.current = true;
      stopScanner().then(() => lookup(decodedText, { fromScan: true }));
    },
    [lookup, stopScanner]
  );

  const startScanner = async () => {
    setStarting(true);
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } }, handleScan, () => {});
      setScanning(true);
    } catch {
      toast.error(t("Kunne ikke starte kameraet. Gi tilgang til kamera, eller skriv inn koden under."));
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    return () => {
      const scanner = scannerRef.current;
      if (scanner) {
        try {
          if (scanner.isScanning) scanner.stop().catch(() => {});
        } catch {
          // ignorer
        }
      }
    };
  }, []);

  const toggleHandout = async (w) => {
    const key = `${w.prize_id}-${w.slot}`;
    setBusySlot(key);
    try {
      const { data } = await api.post(`/my/basar/${id}/handout/${w.prize_id}/${w.slot}`, { handed_out: !w.handed_out_at });
      if (data.order) setResult(data.order);
      if (data.basar) setBasar(data.basar);
      toast.success(w.handed_out_at ? t("Utlevering angret") : t("Premien er merket som utlevert"));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusySlot("");
    }
  };

  const resetView = () => {
    setResult(null);
    setCheckError("");
    setCode("");
    if (orderId) navigate(dashboardBasarCheckUrl(id));
  };

  // Utleveringsliste (PDF) – henta med innlogging og opna i ny fane (fallback: nedlasting)
  const openHandoutList = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/my/basar/${id}/handout-list.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `utleveringsliste-${(basar?.name || "basar").toLowerCase().replace(/[^a-z0-9æøå]+/gi, "-").replace(/^-|-$/g, "")}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPdfBusy(false);
    }
  };

  const statusMeta = (o) => {
    if (!o) return null;
    if (o.status === "paid") return { tone: "success", Icon: CheckCircle2, label: t("Gyldig – loddene er betalt") };
    if (o.status === "pending_payment" || o.status === "processing") return { tone: "info", Icon: Clock, label: t("Ikke betalt ennå – venter på Vipps") };
    if (o.status === "refunded" || o.status === "refunding") return { tone: "danger", Icon: XCircle, label: t("Ugyldig – ordren er refundert") };
    return { tone: "danger", Icon: XCircle, label: t("Ugyldig – ordren er kansellert") };
  };
  const toneClass = { success: "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]", info: "border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]", danger: "border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]" };

  const st = statusMeta(result);
  const wonNumbers = new Set((result?.won || []).map((w) => w.number));
  const handedOut = (result?.won || []).filter((w) => w.handed_out_at).length;

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="basar-check-page">
        <BasarNav basar={basar} />
        {loading || !basar ? (
          <div className="space-y-6" data-testid="basar-loading-state"><Skeleton className="h-40 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-heading font-semibold tracking-tight">{t("Utlevering av premier")}</h2>
                  <p className="text-sm text-muted-foreground">{t("Skann QR-koden på kjøperens kvittering (ordresiden eller PDF-en), eller skriv inn ordrenummer eller loddnummer. Etter publisert trekning kan du merke premiene som utlevert.")}</p>
                </div>
                {basar.draw_published_at && (
                  <Button variant="secondary" size="sm" onClick={openHandoutList} disabled={pdfBusy} className="shrink-0" data-testid="basar-check-handout-list-button">{pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />} {t("Utleveringsliste (PDF)")}</Button>
                )}
              </div>
              {/* Kamera */}
              <Card className="border-border overflow-hidden">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-heading font-semibold flex items-center gap-2"><Camera className="w-4 h-4" /> {t("Skann QR-kode")}</h3>
                    {scanning ? (
                      <Button variant="secondary" size="sm" onClick={stopScanner} data-testid="basar-check-stop-camera-button"><CameraOff className="w-4 h-4 mr-1.5" /> {t("Stopp kamera")}</Button>
                    ) : (
                      <Button size="sm" onClick={startScanner} disabled={starting} data-testid="basar-check-start-camera-button">{starting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />} {t("Start kamera")}</Button>
                    )}
                  </div>
                  <div id="qr-reader" data-testid="basar-check-camera-region" className={`rounded-xl bg-ink/95 ${scanning ? "h-[320px]" : "h-[150px]"} flex items-center justify-center overflow-hidden`}>
                    {!scanning && (
                      <div className="text-center text-primary-foreground/70 px-6">
                        <ScanLine className="w-7 h-7 mx-auto mb-2" />
                        <p className="text-sm">{t("Trykk «Start kamera» for å skanne kvitteringen.")}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              {/* Manuell kode */}
              <Card className="border-border">
                <CardContent className="p-5 space-y-3">
                  <Label htmlFor="basar-check-code">{t("Eller skriv inn kode")}</Label>
                  <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      lookup(code);
                    }}
                  >
                    <Input id="basar-check-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("Ordrenummer (L-…) eller loddnummer")} autoComplete="off" data-testid="basar-check-code-input" />
                    <Button type="submit" disabled={checking || !code.trim()} className="shrink-0 whitespace-nowrap" data-testid="basar-check-lookup-button">{checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}<span className="ml-1.5">{t("Slå opp")}</span></Button>
                  </form>
                  <p className="text-xs text-muted-foreground">{t("Loddnummer finner ordren som kjøpte loddet. Du kan også lime inn lenken fra QR-koden.")}</p>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {checkError && (
                <Alert className="border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10" data-testid="basar-check-error">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription className="flex flex-wrap items-center justify-between gap-2">{checkError} <Button variant="outline" size="sm" onClick={resetView} data-testid="basar-check-error-reset-button"><RotateCcw className="w-4 h-4 mr-1.5" /> {t("Prøv igjen")}</Button></AlertDescription>
                </Alert>
              )}
              {checking && !result && !checkError && <div className="space-y-3" data-testid="basar-check-loading"><Skeleton className="h-14 rounded-xl" /><Skeleton className="h-48 rounded-xl" /></div>}
              {!result && !checking && !checkError && (
                <Card className="border-dashed border-border" data-testid="basar-check-empty">
                  <CardContent className="p-8 text-center space-y-2">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"><PackageCheck className="w-6 h-6" /></span>
                    <p className="font-heading font-semibold">{t("Ingen kvittering valgt")}</p>
                    <p className="text-sm text-muted-foreground">{basar.draw_published_at ? t("Skann en kvittering for å se gevinster og merke premier som utlevert.") : t("Trekningen er ikke publisert ennå. Du kan likevel kontrollere kvitteringer – gevinster vises her etter publisering.")}</p>
                  </CardContent>
                </Card>
              )}
              {result && st && (
                <Card className="border-border" data-testid="basar-check-result" data-status={result.status}>
                  <CardContent className="p-5 sm:p-6 space-y-5">
                    <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${toneClass[st.tone]}`} data-testid="basar-check-status" data-tone={st.tone}>
                      <st.Icon className="w-5 h-5 shrink-0" />
                      <p className="font-heading font-semibold">{st.label}</p>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 space-y-1 text-sm">
                        <p className="text-lg font-heading font-semibold truncate" data-testid="basar-check-buyer-name">{result.name}</p>
                        <p className="text-muted-foreground inline-flex items-center gap-1.5 tabular-nums"><Phone className="w-3.5 h-3.5" /> {result.phone}</p>
                        {result.email && <p className="text-muted-foreground inline-flex items-center gap-1.5 break-all"><Mail className="w-3.5 h-3.5 shrink-0" /> {result.email}</p>}
                        <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><ReceiptText className="w-3.5 h-3.5" /> {t("Ordre {number}", { number: result.order_number })} · {basarPaymentLabel(result)}{result.paid_at ? ` · ${formatDateTime(result.paid_at)}` : ""}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={resetView} className="shrink-0" data-testid="basar-check-next-button"><ScanLine className="w-4 h-4 mr-1.5" /> {t("Skann neste")}</Button>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">{(result.numbers || []).length === 1 ? t("Loddnummer") : t("Loddnumre ({n})", { n: (result.numbers || []).length })}</p>
                      <div className="flex flex-wrap gap-2" data-testid="basar-check-numbers">
                        {(result.numbers || []).map((n) => (
                          <span key={n} className={`inline-flex items-center justify-center min-w-[3rem] rounded-lg border px-3 py-1.5 font-heading font-semibold tabular-nums ${wonNumbers.has(n) ? "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/18 ring-2 ring-[hsl(var(--warning))]/40" : "border-border bg-muted/50"}`} data-testid={`basar-check-number-${n}`}>{n}</span>
                        ))}
                      </div>
                    </div>
                    {/* Gevinstar + utlevering */}
                    <div className="space-y-3" data-testid="basar-check-wins">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="font-heading font-semibold inline-flex items-center gap-2"><Trophy className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Gevinster")}</h3>
                        {result.draw_published && (result.won || []).length > 0 && <Badge variant="outline" className="tabular-nums" data-testid="basar-check-handout-count">{t("{done} av {total} utlevert", { done: handedOut, total: result.won.length })}</Badge>}
                      </div>
                      {!result.draw_published && (
                        <p className="text-sm text-muted-foreground inline-flex items-start gap-2" data-testid="basar-check-not-drawn"><Info className="w-4 h-4 mt-0.5 shrink-0" /> {t("Trekningen er ikke publisert ennå – gevinster og utlevering vises her etter publisering.")}</p>
                      )}
                      {result.draw_published && (result.won || []).length === 0 && <p className="text-sm text-muted-foreground" data-testid="basar-check-no-wins">{t("Ingen gevinst på disse loddene.")}</p>}
                      {(result.won || []).map((w) => {
                        const key = `${w.prize_id}-${w.slot}`;
                        const done = !!w.handed_out_at;
                        return (
                          <div key={key} className={`rounded-xl border p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${done ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/8" : "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10"}`} data-testid="basar-check-win-row" data-handed-out={done ? "true" : "false"}>
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              {w.image_url ? (
                                <img src={imageSrc(w.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0 border border-border" />
                              ) : (
                                <span className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] shrink-0"><Gift className="w-6 h-6" /></span>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium truncate" data-testid="basar-check-win-prize">{w.prize_name}{w.value ? <span className="text-muted-foreground font-normal"> · {nok(w.value)}</span> : null}</p>
                                <p className="text-sm text-muted-foreground tabular-nums">{t("Lodd {n}", { n: w.number })}</p>
                                {done && <p className="text-xs text-[hsl(var(--success))]" data-testid="basar-check-win-handed-out">{t("Utlevert {date}", { date: formatDateTime(w.handed_out_at) })}{w.handed_out_by ? ` · ${w.handed_out_by}` : ""}</p>}
                              </div>
                            </div>
                            {result.status === "paid" && (
                              done ? (
                                <Button variant="ghost" size="sm" onClick={() => toggleHandout(w)} disabled={busySlot !== ""} className="shrink-0 self-start sm:self-center" data-testid="basar-check-undo-handout-button">
                                  {busySlot === key ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1.5" />} {t("Angre")}
                                </Button>
                              ) : (
                                <Button size="sm" onClick={() => toggleHandout(w)} disabled={busySlot !== ""} className="shrink-0 self-start sm:self-center" data-testid="basar-check-handout-button">
                                  {busySlot === key ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1.5" />} {t("Merk som utlevert")}
                                </Button>
                              )
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="border-t border-border pt-3 flex flex-wrap gap-2">
                      <Button asChild variant="ghost" size="sm" data-testid="basar-check-sales-link"><Link to={`${dashboardBasarUrl(id, "sal")}?q=${encodeURIComponent(result.order_number || "")}`}><ReceiptText className="w-4 h-4 mr-1.5" /> {t("Vis i salgslisten")}</Link></Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
